import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import useOrganizationMedicalProfiles from '../../../../../hooks/api/useOrganizationMedicalProfiles';
import useOrganizationGroups from '../../../../../hooks/api/useOrganizationGroups';
import EditClientProfile, { useSelectedGroupIds } from '../../../../shared/ClientProfile/EditClientGroups';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { getIntl } from '../../../../../utils/intl';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { useOrganizationClientRecords } from '../../../../../hooks/api/useOrganizationClientRecords';

const intl = getIntl('client-profile-details-screen', {
    save: 'Save',
});

export type EditClientProfileScreenOwnProps = {};

export type EditClientProfileScreenProps = EditClientProfileScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'edit-client-profile-groups'>;
};

const EditClientProfileScreen: React.FC<EditClientProfileScreenProps> = (props) => {
    const goBack = useGoBack();

    const {
        isLoadingClientRecordGroups,
        clientRecordGroups,
        updateOrganizationClientRecordGroups,
        isUpdatingOrganizationClientRecordGroups,
        clientRecord: clientRecord,
    } = useOrganizationClientRecords({
        organizationClientRecordId: props.route.params.clientRecordId,
        onUpdateOrganizationClientRecordGroupsSuccess: goBack,
    });

    const profileId = clientRecord?.status === 'active' ? clientRecord.clientMedicalProfileId : undefined;
    const { isLoadingOrganizationMedicalProfile, organizationMedicalProfile } = useOrganizationMedicalProfiles({
        medicalProfileId: profileId,
    });

    const { isLoadingGroups, groups } = useOrganizationGroups({});
    const [selectedGroupIds, setSelectedGroupIds] = useSelectedGroupIds({
        clientRecordGroups: clientRecordGroups,
        organizationGroups: groups,
    });

    const isLoading =
        isLoadingOrganizationMedicalProfile ||
        isLoadingClientRecordGroups ||
        isLoadingGroups ||
        isUpdatingOrganizationClientRecordGroups;

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <ActionButtonLayout
                    scrollable
                    actions={[
                        {
                            title: intl('save'),
                            icon: 'CheckCircle',
                            onPress: () =>
                                updateOrganizationClientRecordGroups({
                                    clientRecordId: props.route.params.clientRecordId,
                                    organizationGroupIds: selectedGroupIds,
                                }),
                            isDisabled: isLoading,
                            testID: 'form-submit-button',
                        },
                    ]}
                >
                    <MainContentContainer>
                        <EditClientProfile
                            clientProfile={organizationMedicalProfile!}
                            clientRecordGroups={clientRecordGroups}
                            organizationGroups={groups}
                            selectedGroupIds={selectedGroupIds}
                            setSelectedGroupIds={setSelectedGroupIds}
                            clientFirstName={
                                organizationMedicalProfile?.contactDetails?.firstName ||
                                clientRecord?.clientDetails?.firstName ||
                                ''
                            }
                            clientLastName={
                                organizationMedicalProfile?.contactDetails?.lastName ||
                                clientRecord?.clientDetails?.lastName ||
                                ''
                            }
                        />
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(EditClientProfileScreen);
